import { DateTime } from 'luxon';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Dict, Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateArray, validateBoolean, validateNumber, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { ProductionMeter } from '/imports/api/productionMeters';
import { objectKeys } from '/imports/utils';
import { GmapsAddress } from '/imports/gmaps';

export interface SolarBill extends Doc {
  orgId: string;
  meterIds: string[];
  startDate: string;
  endDate: string;
  issuedDate: string;
  dueDate: string;
  production: number;
  historicalProduction: { key: string, label: string, production: number }[];
  ppaRate: number;
  ppaCharges: number;
  fixedCharges: number;
  previousCharges: number;
  lateCharges: number;
  credits: number;
  accountNumber: string;
  customerName: string;
  type: 'realtyIncome' | 'northshore' | 'northshoreConsolidated' | 'calpine';
  pdf?: FileMeta;
  batch?: number;
  recipients?: string;
  meterIdentifier?: string;
  _meters: ProductionMeter[];
  serviceAddress?: GmapsAddress;
  extra?: any;
}
export type Model = SolarBill;

export const SolarBills = new Mongo.Collection<SolarBill>('solarBills');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: SolarBills,
  editableFields: ['active', 'orgId', 'meterIds', 'startDate', 'endDate', 'issuedDate', 'dueDate', 'production', 'historicalProduction',
    'ppaRate', 'ppaCharges', 'fixedCharges', 'previousCharges', 'lateCharges', 'credits', 'accountNumber', 'customerName', 'serviceAddress', 'pdf',
  ],
  fieldLimits: {
    'accountNumber': 1024, 'customerName': 256, 'dueDate': 64, 'endDate': 64, 'issuedDate': 64, 'meterIds': 1024, 'orgId': 64,
    'startDate': 64,
  },
  isOrgOwned: true,
  listMethod: DeclareMethod<ListMethodArg<SolarBill>, ListMethodResponse<SolarBill, Refs>>('listSolarBills'),
  getMethod: DeclareMethod<string, GetMethodResponse<SolarBill, Refs>>('getSolarBill'),
  createMethod: DeclareMethod<Partial<SolarBill>, string>('createSolarBill'),
  updateMethod: DeclareMethod<Partial<SolarBill>, string>('updateSolarBill'),
  deleteMethod: DeclareMethod<string>('deleteSolarBill'),
  pathPrefix: '/solar-bills',
  asString: (document) => document.name || document._id,
  modelName: strings.SolarBill(),
  pluralName: strings.SolarBills(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { accountNumber: new RegExp(search, 'gi') },
        { issuedDate: new RegExp(search, 'gi') },
        { batch: parseInt(search) },
        { 'serviceAddress.formatted': new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (project: Partial<SolarBill>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'orgId', { required: true });
    validateArray(errors, project, 'meterIds', { required: true });
    validateString(errors, project, 'startDate', { required: true });
    validateString(errors, project, 'endDate', { required: true });
    validateString(errors, project, 'issuedDate', { required: true });
    validateString(errors, project, 'dueDate', { required: true });
    validateNumber(errors, project, 'production', { required: true });
    validateArray(errors, project, 'historicalProduction', { required: true });
    validateNumber(errors, project, 'ppaRate', { required: true });
    validateNumber(errors, project, 'ppaCharges', { required: true });
    validateNumber(errors, project, 'fixedCharges', { required: true });
    validateNumber(errors, project, 'previousCharges', { required: true });
    validateNumber(errors, project, 'lateCharges', { required: true });
    validateNumber(errors, project, 'credits', { required: true });
    validateString(errors, project, 'accountNumber', { required: true });
    validateString(errors, project, 'customerName', { required: true });
    validateString(errors, project, 'type', { required: true });
    return errors;
  },
};
export const getSolarBillForJwt = DeclareMethod<{ billId: string | null, jwt: string | null }, SolarBill>('getSolarBillForJwt');
export const getSolarBillsForJwtAndAccessCode = DeclareMethod<{ jwt: string | null, accessCode: string }, SolarBill[]>('getSolarBillsForJwtAndAccessCode');
export const getSolarBillsBatchStatus = DeclareMethod<{ batch: number }, { generated: number, total: number }>('getSolarBillsBatchStatus');
export const generateSolarBillPdf = DeclareMethod<string, void>('generateSolarBillPdf');

/** Total amount due on a bill (fixed + PPA + previous + late charges, less credits). */
export const solarBillAmountDue = (bill: SolarBill) => (bill.fixedCharges || 0) + (bill.ppaCharges || 0) + (bill.previousCharges || 0) + (bill.lateCharges || 0) - (bill.credits || 0);
/** Solar-related charges on a bill (fixed + PPA charges). */
export const solarBillSolarCharges = (bill: SolarBill) => (bill.fixedCharges || 0) + (bill.ppaCharges || 0);

/**
 * Maps the 13 months ending at the given date's month to short month labels, e.g. {'2024-04-01': 'Apr', ...}.
 * @param dateStr - ISO date anchoring the last month
 * @returns dict of 'yyyy-MM-01' keys to abbreviated month labels
 */
const thirteenMonthKeys = (dateStr: string) => {
  const start = DateTime.fromISO(dateStr).startOf('month').minus({ months: 12 });
  return Object.fromEntries(
    Array.from({ length: 13 }, (_, i) => {
      const d = start.plus({ months: i });
      return [d.toFormat('yyyy-MM-01'), d.toFormat('MMM')];
    })
  );
};

/**
 * Builds a 13-month historical production series for the given meters, adding the current bill's production into the start month.
 * @param startDate - the current bill's start date
 * @param meterIds - meters to aggregate historical bills for
 * @param production - the current bill's production to fold into the start month
 * @returns array of { key, label, production } per month
 */
export const historicalBillProduction = async (startDate: string, meterIds: string[], production: number) => {
  const historicalMonthDict = thirteenMonthKeys(startDate);
  const historicalMonthDates = objectKeys(historicalMonthDict);
  const firstHistoricalDate = historicalMonthDates.at(0);
  const lastHistoricalDate = historicalMonthDates.at(-1);
  const historicalQuery = { meterIds: { $in: meterIds }, startDate: { $gte: firstHistoricalDate, $lt: lastHistoricalDate } };
  const historicalBills = await SolarBills.find(historicalQuery).fetchAsync();
  const historicalBillsMap = historicalBills.reduce((map: Dict<SolarBill[]>, b) => {
    const key = b.startDate.substring(0, 8);
    if (!map[key]) map[key] = [];
    map[key].push(b);
    return map;
  }, {});
  return objectKeys(historicalMonthDict).map((date) => {
    const k = date.substring(0, 8);
    const _production = (historicalBillsMap[k] || []).reduce((t, b) => t + (b.production || 0), 0);
    const obj = { key: date, label: historicalMonthDict[date], production: _production };
    if (k === startDate.substring(0, 8)) obj.production += production;
    return obj;
  });
};

Meteor.isServer && Meteor.startup(async () => {
  await SolarBills.createIndexAsync({ orgId: 1 });
  await SolarBills.createIndexAsync({ accountNumber: 1 });
  await SolarBills.createIndexAsync({ batch: 1 });
  await SolarBills.createIndexAsync({ 'serviceAddress.formatted': 1 }, { sparse: true });
  await SolarBills.createIndexAsync({ 'serviceAddress.state': 1 }, { sparse: true });
});